/**
 * Consent record entries shared by the web onboarding checkouts
 *
 * Builds the `termsData` entries of `recordPatientTermsAcceptance` whose wording is shared between checkouts or read
 * back by the Cloud Functions. The acceptance PDF labels each entry "Checkbox-Text:" and prints `infoTitle`,
 * `infoText` and `infoUrl` below it.
 *
 * References:
 * - `hooks/useBnplPaymentScreen.ts`, `hooks/useInsuranceCheckoutScreen.ts` — the two checkouts recording them
 * - `physiofit_cloud_functions/functions/fn/bnpl/flow/completionPaymentLookups.ts` — reads the provider entry back
 */

import type { PatientTermsAcceptanceCheckbox } from '@physiofit-digital/physiofit_types';
import privacyDocumentHtml from '../content/privacy/datenschutzinformation-nutzer.html?raw';

/**
 * Title of the provider privacy entry. `readOnboardingConsent` in the Cloud Functions finds the entry by this title
 * and `type: 'other'`; the string is the contract, keep both copies in sync.
 */
const PROVIDER_PRIVACY_TERM_TITLE = 'Datenschutzbestimmung der RBI';

/**
 * The version of the privacy document the drawer shows, from its "Zuletzt aktualisiert" line, and the copy of that
 * version that `build-privacy-html.py` keeps at `/datenschutz-YYYY-MM-DD.html`. Undefined only if the line is missing
 * from the document; the record then links the current page.
 */
function resolvePrivacyDocumentVersion(): { label: string; url: string } | undefined {
  const match = /Zuletzt aktualisiert: (\d{2})\.(\d{2})\.(\d{4})/.exec(privacyDocumentHtml);
  if (!match) {
    return undefined;
  }
  const [, day, month, year] = match;
  return {
    label: `${day}.${month}.${year}`,
    url: `${window.location.origin}/datenschutz-${year}-${month}-${day}.html`,
  };
}

const privacyDocumentVersion = resolvePrivacyDocumentVersion();

/**
 * The Physiofit privacy notice, recorded although nobody ticks it: it reaches the patient as the footer note
 * (`PhysiofitPrivacyNote`), which states that it applies rather than asking for consent. "Hinweis angezeigt" keeps the
 * PDF from reading as a tick nobody gave; the version and its dated copy say which text the note pointed to.
 */
export const PHYSIOFIT_PRIVACY_NOTICE_TERM: Omit<PatientTermsAcceptanceCheckbox, 'acceptedDate'> = {
  type: 'other',
  checkboxText: 'Hinweis angezeigt: Es gilt die Datenschutzerklärung der Physiofit Digital GmbH.',
  title: 'Datenschutzhinweis',
  infoTitle: privacyDocumentVersion
    ? `Datenschutzinformation Nutzer, Fassung vom ${privacyDocumentVersion.label}`
    : 'Datenschutzinformation Nutzer',
  infoUrl: privacyDocumentVersion?.url ?? `${window.location.origin}/datenschutz`,
};

/**
 * The ticked provider privacy box. The box always renders the same sentence; the server compares the cached
 * document's `consentText`, which can differ from that sentence, with the provider's current text. Both are recorded.
 *
 * @param {Object} params The rendered checkbox text, the provider document's `consentText`, the link the box
 * rendered (only a validated http(s) URL) and the acceptance date.
 *
 * @return {PatientTermsAcceptanceCheckbox} The entry.
 */
export function buildProviderPrivacyTerm(params: {
  checkboxText: string;
  consentText: string;
  link?: string;
  acceptedDate: string;
}): PatientTermsAcceptanceCheckbox {
  const { checkboxText, consentText, link, acceptedDate } = params;
  return {
    type: 'other',
    checkboxText,
    title: PROVIDER_PRIVACY_TERM_TITLE,
    infoTitle: 'Text des Zahlungsanbieters',
    infoText: consentText,
    ...(link && { infoUrl: link }),
    acceptedDate,
  };
}
